import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {useToast} from '../hooks/use-toast'
import {useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * Custom hook that provides a function to add items to the shopping cart.
 * It handles both individual products and product sets, updating the cart accordingly.
 * Displays a success or error toast message based on the outcome of the operation.
 *
 * @returns {Function} addToCart - A function that adds items to the cart.
 *
 * @param {Object} item - The product item to be added to the cart. It can be a single product or a set of products.
 * @param {string} item.id - The unique identifier for the product.
 * @param {string} item.productId - An alternative identifier for the product.
 * @param {number} item.price - The price of the product.
 * @param {Array} [item.setProducts] - An array of child products if the item is a set.
 * @param {string} item.setProducts[].id - The unique identifier for the child product.
 * @param {string} item.setProducts[].productId - An alternative identifier for the child product.
 * @param {number} item.setProducts[].price - The price of the child product.
 * @param {number} quantity - The number of items to add to the cart.
 *
 * @example
 * const addToCart = useAddToCart();
 * addToCart({id: '123', price: 29.99}, 2);
 *
 * @example
 * const addToCart = useAddToCart();
 * addToCart({setProducts: [{id: '123', price: 29.99}, {id: '124', price: 39.99}]}, 1);
 */
export const useAddToCart = () => {
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const showToast = useToast()
    const {formatMessage} = useIntl()
    const {triggerAddToCartEvent} = useGTM()

    const addToCart = async (item, quantity) => {
        const isAddingASet = Boolean(item.setProducts)
        const productItems = isAddingASet
            ? item.setProducts.map((child) => ({
                  productId: child.id || child.productId,
                  price: child.price,
                  quantity
              }))
            : [
                  {
                      productId: item.id || item.productId,
                      price: item.price,
                      quantity
                  }
              ]

        try {
            await addItemToNewOrExistingBasket(productItems)
            triggerAddToCartEvent(item, productItems[0])

            // Return enriched items with full product and variant details
            return productItems.map((productItem) => ({
                product: {
                    ...item
                },
                variant: {
                    orderable: item.orderable,
                    price: item.price,
                    productId: productItem.productId,
                    tieredPrices: item.tieredPrices,
                    variationValues: item.variationValues
                },
                quantity: productItem.quantity
            }))
        } catch (e) {
            showToast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        }
    }

    return addToCart
}
